import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Space, Toast, Button } from 'antd-mobile';
import { ExclamationCircleOutline } from 'antd-mobile-icons';
import styled from 'styled-components';
import { useAppSelector, useAppDispatch } from '../../hooks/reduxHooks';
import {
  selectScooters,
  selectCurrentScooter,
  selectScooterLoading,
  selectScooterError,
  clearError,
} from '../../store/slices/scooterSlice';
import {
  selectWalletBalance,
  selectWalletLoading,
  selectWalletError,
  fetchWalletBalance,
  fetchRecentTransactions,
  showTopupModal,
} from '../../store/slices/walletSlice';
import { selectUserType } from '../../store/slices/authSlice';
import { isFleetType } from '../../types/user';
import ActivePackCard from './ActivePackCard';
import WalletCard from './WalletCard';
import PackRecommendations from './PackRecommendations';
import FleetScooterPackCard from './FleetScooterPackCard';
import ScooterPackPurchaseModal from './ScooterPackPurchaseModal';
import EnergyPackPurchaseModal from './EnergyPackPurchaseModal';
import RideReadinessPanel from '../molecules/RideReadinessPanel';
import { packService } from '../../services/api';
import type {
  ActivePack,
  PackDashboardItem,
  PackDashboardResponse,
  PackTemplate,
} from '../../services/api/types';
import type { ScooterPack, ScooterPackPurchaseResponse } from '../../services/api/scooterPackService';

const DashboardContainer = styled.div`
  padding: 0;
  min-height: 100vh;
  background: #f5f5f5;
`;

const RefreshIndicator = styled.div`
  position: fixed;
  top: 60px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  z-index: 1000;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  &.visible {
    opacity: 1;
  }
`;

const ErrorBanner = styled.div`
  background: #fff2f0;
  border: 1px solid #ffccc7;
  color: #ff4d4f;
  padding: 12px 16px;
  margin-bottom: 16px;
  border-radius: 6px;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(2px);
`;

const LoadingContent = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  text-align: center;
  font-size: 14px;
  color: #666;
`;

interface PackNeedState {
  energyOverageAh: number;
  scooterOverageKm: number;
}

const emptyPackNeed: PackNeedState = {
  energyOverageAh: 0,
  scooterOverageKm: 0,
};

const PACK_DASHBOARD_NOT_AVAILABLE = 'Pack dashboard not available';

const toNumber = (value: number | string | null | undefined, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const hoursRemainingFromExpiry = (expiresAt?: string | null) => {
  if (!expiresAt) return 0;
  return Math.max(0, (new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60));
};

const activePackStatusColor = (pack: ActivePack): ActivePack['status_color'] => {
  if (pack.status === 'reserved' || pack.status === 'queued') return 'grey';
  if (pack.status !== 'active') return 'black';

  const ahPercent = pack.ah_limit ? (pack.ah_remaining / pack.ah_limit) * 100 : 100;
  const timePercent = pack.validity_hours ? (pack.hours_remaining / pack.validity_hours) * 100 : 100;
  const minimumPercent = Math.min(ahPercent, timePercent);

  if (minimumPercent <= 20) return 'red';
  if (minimumPercent <= 50) return 'orange';
  return 'green';
};

const dashboardEnergyToActivePack = (item: PackDashboardItem): ActivePack => {
  const isQueued = item.status_group === 'queued';
  const validityHours = item.validity_hours ?? undefined;
  const hoursRemaining = item.hours_remaining != null
    ? toNumber(item.hours_remaining)
    : hoursRemainingFromExpiry(item.expires_at);
  const ahLimit = item.energy_limit_ah != null ? toNumber(item.energy_limit_ah) : undefined;
  const ahUsed = toNumber(item.energy_used_ah);
  const ahRemaining = item.energy_remaining_ah != null
    ? toNumber(item.energy_remaining_ah)
    : Math.max(0, (ahLimit ?? 0) - ahUsed);

  const pack: ActivePack = {
    pack_id: item.pack_id,
    template_id: item.template_id,
    template_name: item.template_name,
    pack_type: item.pack_type as ActivePack['pack_type'],
    scooter_vin: item.scooter_vin,
    status: item.status,
    price_npr: item.price_npr != null ? toNumber(item.price_npr) : undefined,
    ah_limit: ahLimit,
    ah_used: ahUsed,
    ah_remaining: ahRemaining,
    validity_hours: validityHours,
    hours_used: isQueued ? 0 : validityHours ? Math.max(0, validityHours - hoursRemaining) : 0,
    hours_remaining: hoursRemaining,
    created_at: item.created_at,
    activated_at: item.activated_at ?? undefined,
    expires_at: item.expires_at ?? undefined,
    designated_next: item.designated_next,
    status_color: item.status_color ?? 'green',
  };

  return {
    ...pack,
    status_color: item.status_color ?? activePackStatusColor(pack),
  };
};

const dashboardScooterToScooterPack = (item: PackDashboardItem): ScooterPack => ({
  pack_id: item.pack_id,
  template_id: item.template_id,
  template_name: item.template_name,
  scooter_vin: item.scooter_vin,
  status: item.status as ScooterPack['status'],
  pack_type: item.pack_type as ScooterPack['pack_type'],
  price_npr: toNumber(item.price_npr),
  validity_hours: item.validity_hours ?? undefined,
  distance_limit_km: item.distance_limit_km != null ? toNumber(item.distance_limit_km) : undefined,
  distance_used_km: item.distance_used_km != null ? toNumber(item.distance_used_km) : undefined,
  activation_date: item.activated_at ?? undefined,
  expiry_date: item.expires_at ?? undefined,
  designated_next: item.designated_next,
  created_at: item.created_at,
  updated_at: undefined,
});

// Helper function to safely show toast messages
const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
  try {
    Toast.show(message);
  } catch {
    console.log(`[${type.toUpperCase()}] ${message}`);
  }
};

const dashboardSummaryErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  if (error?.response?.status === 404 && detail === PACK_DASHBOARD_NOT_AVAILABLE) {
    return 'Could not verify required packs for this scooter.';
  }
  return detail || error?.message || 'Failed to load pack dashboard summary';
};

const MultiScooterDashboard: React.FC = () => {
  console.log('🚀 MultiScooterDashboard: Component mounted/rendering');
  
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  // Scooter state
  const scooters = useAppSelector(selectScooters);
  const currentScooter = useAppSelector(selectCurrentScooter);
  const scooterLoading = useAppSelector(selectScooterLoading);
  const scooterError = useAppSelector(selectScooterError);
  
  // Debug logging
  console.log('🎯 MultiScooterDashboard: Full Redux state:', {
    scooters,
    scootersLength: scooters.length,
    currentScooter,
    scooterLoading,
    scooterError
  });
  
  // Wallet state
  const walletBalance = useAppSelector(selectWalletBalance);
  const walletLoading = useAppSelector(selectWalletLoading);
  const walletError = useAppSelector(selectWalletError);

  // User state
  const userType = useAppSelector(selectUserType);
  const isFleetUser = isFleetType(userType);

  // Local state
  const [featuredPacks, setFeaturedPacks] = useState<PackTemplate[]>([]);
  const [packsLoading, setPacksLoading] = useState(false);
  const [packNeed, setPackNeed] = useState<PackNeedState>(emptyPackNeed);
  const [activeEnergyPack, setActiveEnergyPack] = useState<ActivePack | null>(null);
  const [queuedEnergyPacks, setQueuedEnergyPacks] = useState<ActivePack[]>([]);
  const [activeScooterPack, setActiveScooterPack] = useState<ScooterPack | null>(null);
  const [queuedScooterPacks, setQueuedScooterPacks] = useState<ScooterPack[]>([]);
  const [readinessReady, setReadinessReady] = useState<boolean | null>(null);
  const [scooterPackLoading, setScooterPackLoading] = useState(false);
  const [scooterPackError, setScooterPackError] = useState<string | null>(null);
  const [packNeedError, setPackNeedError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showEnergyPackModal, setShowEnergyPackModal] = useState(false);
  const [selectedEnergyTemplateId, setSelectedEnergyTemplateId] = useState<string | null>(null);
  const [showScooterPackModal, setShowScooterPackModal] = useState(false);
  const latestScooterDataRequestRef = useRef(0);
  const selectedScooterVinRef = useRef<string | null>(currentScooter?.vin ?? null);

  const isLatestScooterDataRequest = (requestId: number, requestedVin: string) =>
    latestScooterDataRequestRef.current === requestId &&
    selectedScooterVinRef.current === requestedVin;

  const clearDashboardScooterData = () => {
    setFeaturedPacks([]);
    setPackNeed({ ...emptyPackNeed });
    setActiveEnergyPack(null);
    setQueuedEnergyPacks([]);
    setActiveScooterPack(null);
    setQueuedScooterPacks([]);
    setReadinessReady(null);
    setScooterPackError(null);
    setPackNeedError(null);
  };

  useEffect(() => {
    selectedScooterVinRef.current = currentScooter?.vin ?? null;
  }, [currentScooter?.vin]);

  // Initialize dashboard
  useEffect(() => {
    initializeDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh every 15 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      if (currentScooter) {
        handleRefresh();
      }
    }, 15 * 60 * 1000); // 15 minutes

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScooter]);

  // Load data when scooter changes
  useEffect(() => {
    if (currentScooter?.vin) {
      clearDashboardScooterData();
      loadScooterSpecificData(currentScooter.vin);
    } else {
      latestScooterDataRequestRef.current += 1;
      clearDashboardScooterData();
      setPacksLoading(false);
      setScooterPackLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScooter?.vin]);

  const initializeDashboard = async () => {
    console.log('🔍 MultiScooterDashboard: Initializing dashboard...');
    try {
      // Scooter list/selection is owned by MobileLayout. Dashboard initializes
      // only the wallet data it consumes directly.
      await Promise.all([
        dispatch(fetchWalletBalance()),
        dispatch(fetchRecentTransactions(5)),
      ]);
    } catch (error) {
      console.error('Failed to initialize dashboard:', error);
      showToast('Failed to load dashboard data', 'error');
    }
  };

  const applyDashboardSummary = (summary: PackDashboardResponse) => {
    // Backend still exposes accounting fields as debt; the rider UI frames them as overage.
    const energyPackOverageAh = toNumber(summary.readiness.energy_debt_ah);
    const scooterPackOverageKm = toNumber(summary.readiness.scooter_debt_km);

    setPackNeed({
      energyOverageAh: energyPackOverageAh,
      scooterOverageKm: scooterPackOverageKm,
    });
    setActiveEnergyPack(summary.energy.active ? dashboardEnergyToActivePack(summary.energy.active) : null);
    setQueuedEnergyPacks(summary.energy.queued.map(dashboardEnergyToActivePack));
    setActiveScooterPack(summary.scooter.active ? dashboardScooterToScooterPack(summary.scooter.active) : null);
    setQueuedScooterPacks(summary.scooter.queued.map(dashboardScooterToScooterPack));
    setReadinessReady(summary.readiness.ready);
  };

  const loadScooterSpecificData = async (scooterVin = currentScooter?.vin) => {
    if (!scooterVin) return;

    const requestId = latestScooterDataRequestRef.current + 1;
    latestScooterDataRequestRef.current = requestId;

    try {
      setPacksLoading(true);
      setScooterPackLoading(isFleetUser);
      setScooterPackError(null);
      setPackNeedError(null);

      const [packs, dashboardSummaryResult] = await Promise.all([
        packService.getFeaturedPacks(scooterVin, 3),
        packService.getDashboardPackSummary(scooterVin).then(
          (summary) => ({ summary, error: null }),
          (error) => ({ summary: null, error })
        ),
      ]);

      if (!isLatestScooterDataRequest(requestId, scooterVin)) {
        return;
      }

      console.log('🎯 MultiScooterDashboard: Featured packs received:', packs);
      console.log('🎯 MultiScooterDashboard: Featured packs count:', packs.length);
      setFeaturedPacks(packs);
      if (dashboardSummaryResult.summary) {
        applyDashboardSummary(dashboardSummaryResult.summary);
      } else {
        console.error('Failed to load pack dashboard summary:', dashboardSummaryResult.error);
        setPackNeedError('Could not verify required packs.');
        setScooterPackError(dashboardSummaryErrorMessage(dashboardSummaryResult.error));
        setPackNeed({ ...emptyPackNeed });
        setActiveEnergyPack(null);
        setQueuedEnergyPacks([]);
        setActiveScooterPack(null);
        setQueuedScooterPacks([]);
        setReadinessReady(null);
      }
    } catch (error) {
      if (!isLatestScooterDataRequest(requestId, scooterVin)) {
        return;
      }

      console.error('Failed to load scooter data:', error);
      try {
        Toast.show('Failed to load pack recommendations');
      } catch {
        console.error('Failed to load pack recommendations');
      }
    } finally {
      if (isLatestScooterDataRequest(requestId, scooterVin)) {
        setPacksLoading(false);
        setScooterPackLoading(false);
      }
    }
  };

  const handleScooterPackRetry = async () => {
    if (!currentScooter) return;

    await loadScooterSpecificData(currentScooter.vin);
  };

  const handleRefresh = async () => {
    if (!currentScooter || refreshing) return;

    try {
      setRefreshing(true);
      
      // Refresh wallet and the dashboard-owned pack summary.
      await Promise.all([
        dispatch(fetchWalletBalance()),
        loadScooterSpecificData(currentScooter.vin),
      ]);

    } catch (error) {
      console.error('Failed to refresh data:', error);
      showToast('Failed to refresh data', 'error');
    } finally {
      setRefreshing(false);
    }
  };

  const handleBuyPack = (pack: PackTemplate) => {
    setSelectedEnergyTemplateId(pack.id);
    setShowEnergyPackModal(true);
  };

  const handleLoadWallet = () => {
    dispatch(showTopupModal());
  };

  const handleViewAllPacks = () => {
    setSelectedEnergyTemplateId(null);
    setShowEnergyPackModal(true);
  };

  const handleViewEnergyPacks = () => {
    navigate('/packs?type=energy');
  };

  const handleViewTransactions = () => {
    navigate('/wallet');
  };

  const handleTopUpWallet = () => {
    dispatch(showTopupModal());
  };

  const handleDismissError = () => {
    dispatch(clearError());
  };

  // Fleet scooter pack handlers
  const handleEnergyPackPurchase = () => {
    setSelectedEnergyTemplateId(null);
    setShowEnergyPackModal(true);
  };

  const handleEnergyPackPurchaseSuccess = async () => {
    await handleRefresh();
    setTimeout(() => {
      setSelectedEnergyTemplateId(null);
      setShowEnergyPackModal(false);
    }, 1500);
  };

  const handleEnergyPackModalClose = () => {
    setSelectedEnergyTemplateId(null);
    setShowEnergyPackModal(false);
  };

  const handleScooterPackPurchase = () => {
    setShowScooterPackModal(true);
  };

  // The energy catalog is blocked on a missing scooter pack: hand the rider
  // straight to that flow for the same scooter. Reopening the energy modal
  // afterwards refetches the catalog, so the block clears once it is bought.
  const handleEnergyPackNeedsScooterPack = () => {
    setSelectedEnergyTemplateId(null);
    setShowEnergyPackModal(false);
    setShowScooterPackModal(true);
  };

  const handleScooterPackPurchaseSuccess = async (result: ScooterPackPurchaseResponse) => {
    console.log('📦 Scooter pack purchased:', result);
    // Refresh wallet and scooter data after purchase
    await handleRefresh();
    // Close the modal after a short delay to show success
    setTimeout(() => {
      setShowScooterPackModal(false);
    }, 1500);
  };

  const handleScooterPackModalClose = () => {
    setShowScooterPackModal(false);
  };

  const readinessLoading = scooterLoading.scooters || packsLoading || scooterPackLoading;
  const readinessHasError = !!(packNeedError ?? scooterPackError);
  const readinessCanOwnPurchaseActions = !readinessLoading && !readinessHasError;
  const energyPackNeeded = !activeEnergyPack || packNeed.energyOverageAh > 0;
  const scooterPackNeeded = isFleetUser && (!activeScooterPack || packNeed.scooterOverageKm > 0);
  const selectedListScooter = currentScooter
    ? scooters.find(scooter => scooter.vin === currentScooter.vin)
    : null;
  const selectedAccessState = currentScooter?.access_state ?? selectedListScooter?.access_state;

  // Show loading overlay when switching scooters
  if (scooterLoading.switching) {
    return (
      <DashboardContainer>
        <LoadingOverlay>
          <LoadingContent>
            <div style={{ marginBottom: '12px' }}>🔄</div>
            <div>Switching scooter...</div>
          </LoadingContent>
        </LoadingOverlay>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      {refreshing && (
        <RefreshIndicator className="visible">
          Refreshing data...
        </RefreshIndicator>
      )}

      {(scooterError || walletError) && (
        <ErrorBanner>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ExclamationCircleOutline style={{ marginRight: '8px' }} />
            {scooterError || walletError}
          </div>
          <Button
            fill="none"
            style={{
              color: '#ff4d4f',
              minWidth: '44px',
              minHeight: '44px',
              fontSize: '18px',
              lineHeight: 1,
            }}
            onClick={handleDismissError}
            aria-label="Dismiss error"
          >
            ✕
          </Button>
        </ErrorBanner>
      )}

      <Space direction="vertical" style={{ width: '100%' }}>
        <RideReadinessPanel
          isFleetUser={isFleetUser}
          hasEnergyPack={!!activeEnergyPack}
          hasScooterPack={!!activeScooterPack}
          accessState={selectedAccessState}
          readinessReady={readinessReady}
          energyPackOverageAh={packNeed.energyOverageAh}
          scooterPackOverageKm={packNeed.scooterOverageKm}
          loading={readinessLoading}
          error={packNeedError ?? scooterPackError}
          onBuyEnergyPack={handleEnergyPackPurchase}
          onBuyScooterPack={isFleetUser ? handleScooterPackPurchase : undefined}
        />

        {/* Energy Pack Status */}
        <ActivePackCard
          activePack={activeEnergyPack}
          queuedPacks={queuedEnergyPacks}
          onBuyPack={readinessCanOwnPurchaseActions && energyPackNeeded ? undefined : handleEnergyPackPurchase}
          onViewPacks={handleViewEnergyPacks}
        />

        {/* Fleet Scooter Pack Card - only for fleet users */}
        {isFleetUser && currentScooter && (
          <FleetScooterPackCard
            activePack={activeScooterPack}
            queuedPacks={queuedScooterPacks}
            loading={scooterPackLoading}
            error={scooterPackError}
            onPurchaseClick={readinessCanOwnPurchaseActions && scooterPackNeeded ? undefined : handleScooterPackPurchase}
            onRetry={handleScooterPackRetry}
          />
        )}

        {/* Wallet Balance */}
        <WalletCard
          balance={walletBalance}
          loading={walletLoading.balance}
          onLoadWallet={handleLoadWallet}
          onViewTransactions={handleViewTransactions}
        />

        {/* Featured Pack Recommendations */}
        <PackRecommendations
          featuredPacks={featuredPacks}
          currentWalletBalance={walletBalance?.current_balance || 0}
          loading={packsLoading}
          onBuyPack={handleBuyPack}
          onViewAllPacks={handleViewAllPacks}
          onTopUpWallet={handleTopUpWallet}
          scooterVin={currentScooter?.vin}
        />
      </Space>

      <EnergyPackPurchaseModal
        visible={showEnergyPackModal}
        scooterVin={currentScooter?.vin || ''}
        walletBalance={walletBalance?.current_balance || 0}
        initialTemplateId={selectedEnergyTemplateId}
        energyOverageAh={packNeed.energyOverageAh}
        onClose={handleEnergyPackModalClose}
        onSuccess={handleEnergyPackPurchaseSuccess}
        onNeedScooterPack={
          isFleetUser && currentScooter ? handleEnergyPackNeedsScooterPack : undefined
        }
      />

      {/* Fleet Scooter Pack Purchase Modal */}
      {isFleetUser && currentScooter && (
        <ScooterPackPurchaseModal
          visible={showScooterPackModal}
          scooterVin={currentScooter.vin}
          walletBalance={walletBalance?.current_balance || 0}
          scooterOverageKm={packNeed.scooterOverageKm}
          onClose={handleScooterPackModalClose}
          onSuccess={handleScooterPackPurchaseSuccess}
        />
      )}
    </DashboardContainer>
  );
};

export default MultiScooterDashboard;
